import { useState, type FormEvent } from "react";
import { Link, useLocation } from "react-router-dom";
import { useAuthMethods, useSocialAuthError } from "./session";
import { CircleNotch } from "@phosphor-icons/react";
import { Button } from "../design-system";
import { logger } from "../services/telemetry";
import { beginAuthAttempt, failAuthAttempt } from "./authAttempt";
import {
  trackButtonPressed,
  trackLoginFailed,
  type AuthMethod,
} from "../analytics/tracking";
import { loginErrorMessage, redirectIfEmailNotAllowed } from "./authErrors";
import { useEmailPasswordAuth } from "../services/flags";
import { FORGOT_PASSWORD_PATH, SIGNUP_PATH } from "./paths";
import {
  AuthCard,
  AuthCta,
  AuthField,
  AuthHeading,
  AuthFrame,
  EMAIL_RE,
  GoogleColorIcon,
  PasswordField,
  TermsNotice,
} from "./AuthShared";

/* Sign-in for the hard gate, rendered in the shared SplitScreenLayout: the
 * branded dark left panel (AuthLeftPanel) beside a card with the email/password
 * form, then a divider and the Google social button (redirect flow).
 *
 * Email sign-in resolves through session.tsx's completeLogin: on success the
 * auth status flips and this screen unmounts — a verified user is forwarded by
 * LoginRoute, an unverified one is routed to /check-email. Social sign-in is a
 * full-page redirect: the click navigates away to the provider, so the button
 * just enters its loading state and the page leaves; the return load completes
 * the exchange. A returning redirect that failed its exchange flips socialError
 * and we show the banner here (useSocialAuthError). */

export function LoginScreen() {
  const authMethods = useAuthMethods();
  const socialError = useSocialAuthError();
  // Email/password is flag-gated (igepa — the debugging path); Google is the
  // product default. Held until `ready` so the form doesn't flash in and out.
  const emailAuth = useEmailPasswordAuth();
  const showEmailForm = emailAuth.ready && emailAuth.enabled;
  // Preserve the query (e.g. ?invitation_id=…) when linking to sign-up so an
  // invite deeplink survives the hop from /login to /signup.
  const { search } = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [emailErr, setEmailErr] = useState("");
  const [passwordErr, setPasswordErr] = useState("");
  // Which auth method is in flight (null when idle). Tracks the *specific* CTA
  // clicked so only that button shows a spinner; `busy` still disables the
  // whole form to block a concurrent second attempt.
  const [pending, setPending] = useState<AuthMethod | null>(null);
  const busy = pending !== null;
  const [formErr, setFormErr] = useState<string | null>(null);

  // Shared failure handling for every auth path. On success the auth status
  // flips (authenticated → LoginRoute forwards; unverified → /check-email) and
  // this screen unmounts, so we only clear `pending` on failure. Social
  // sign-in resolves just to start the redirect — the page then navigates away.
  // Fires AuthStarted on the attempt and UserLoginFailed on a rejected one
  // (login success is fired from session.tsx once the session lands).
  const run = async (authMethod: AuthMethod, fn: () => Promise<unknown>) => {
    setFormErr(null);
    // Drop any stale returning-redirect failure so its banner doesn't linger
    // through this attempt.
    authMethods.clearSocialError();
    trackButtonPressed(
      authMethod === "GOOGLE" ? "Continue with Google" : "Sign in",
    );
    beginAuthAttempt(authMethod, "LOGIN");
    setPending(authMethod);
    try {
      await fn();
    } catch (err) {
      /* Terminate the attempt FIRST: the allowlist branch leaves via
       * window.location, so nothing after it runs — which is why allowlist
       * rejections never appeared in the funnel at all. */
      failAuthAttempt(err);

      // Allowlist rejection is terminal — redirect to coming-soon, skip the inline error.
      if (redirectIfEmailNotAllowed(err)) return;

      logger.error(
        err instanceof Error ? err : new Error(String(err)),
        `${authMethod} sign-in failed`,
      );
      trackLoginFailed(authMethod, err);
      setFormErr(loginErrorMessage(err));
      setPending(null);
    }
  };

  const handleSocial = () => run("GOOGLE", () => authMethods.socialLogin());

  const validate = () => {
    const trimmed = email.trim();
    const e = !trimmed
      ? "Enter your email."
      : !EMAIL_RE.test(trimmed)
        ? "Enter a valid email address."
        : "";
    const p = !password ? "Enter your password." : "";
    setEmailErr(e);
    setPasswordErr(p);
    return !e && !p;
  };

  const handleEmail = (e: FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    void run("EMAIL_PASSWORD", () =>
      authMethods.loginWithEmail(email.trim(), password),
    );
  };

  return (
    <AuthFrame>
      <div className="onboarding onboarding-card flex flex-col gap-5">
        <AuthCard>
          <div className="flex flex-col gap-6">
            <div className="flex flex-col gap-1.5">
              <AuthHeading>Sign in</AuthHeading>
              <p className="t-body-sm" style={{ color: "var(--fg-3)" }}>
                {showEmailForm
                  ? "Continue with Google, or use your email and password."
                  : "Continue with your Google account."}
              </p>
            </div>

            {(formErr || socialError) && (
              <div
                role="alert"
                className="flex animate-ai21-rise items-start gap-2 rounded-md border border-error-border bg-error-bg px-3 py-2.5 [&_svg]:size-4 [&_svg]:shrink-0"
              >
                <span className="text-body-sm text-error-fg">
                  {formErr ?? "Couldn't complete sign-in. Please try again."}
                </span>
              </div>
            )}

            {/* Social provider (full-page redirect) — offered first, above the
                email/password form. */}
            <Button
              variant="secondary"
              size="lg"
              className="w-full"
              onClick={handleSocial}
              disabled={busy}
              iconLeft={
                pending === "GOOGLE" ? (
                  <CircleNotch className="animate-spin" />
                ) : (
                  <GoogleColorIcon />
                )
              }
            >
              Continue with Google
            </Button>

            {showEmailForm && (
              <>
                <div className="flex items-center gap-3">
                  <div
                    className="h-px flex-1"
                    style={{ background: "var(--onb-track)" }}
                  />
                  <span className="text-body-sm text-fg-3">or</span>
                  <div
                    className="h-px flex-1"
                    style={{ background: "var(--onb-track)" }}
                  />
                </div>

                <form
                  onSubmit={handleEmail}
                  className="flex flex-col gap-4"
                  noValidate
                >
                  <AuthField
                    id="email"
                    label="Email"
                    type="email"
                    value={email}
                    onChange={(v) => {
                      setEmail(v);
                      if (emailErr) setEmailErr("");
                    }}
                    error={emailErr}
                    placeholder="you@company.com"
                    autoComplete="email"
                    disabled={busy}
                    autoFocus
                  />
                  <PasswordField
                    id="password"
                    label="Password"
                    value={password}
                    onChange={(v) => {
                      setPassword(v);
                      if (passwordErr) setPasswordErr("");
                    }}
                    error={passwordErr}
                    autoComplete="current-password"
                    disabled={busy}
                  />
                  <div className="-mt-2 flex justify-end">
                    <Link
                      to={FORGOT_PASSWORD_PATH}
                      className="text-[12px] no-underline hover:underline"
                      style={{ color: "var(--fg-3)" }}
                      onClick={() => trackButtonPressed("Forgot password")}
                    >
                      Forgot password?
                    </Link>
                  </div>
                  <AuthCta
                    type="submit"
                    className="w-full"
                    disabled={busy}
                    iconLeft={
                      pending === "EMAIL_PASSWORD" ? (
                        <CircleNotch className="animate-spin" />
                      ) : undefined
                    }
                  >
                    Sign in
                  </AuthCta>
                </form>
              </>
            )}

            <TermsNotice />
          </div>
        </AuthCard>

        {/* Google-only mode has no separate sign-up: "Continue with Google"
            provisions a new account by itself, so the cross-link is noise. */}
        {showEmailForm && (
          <p
            className="text-center text-[12px]"
            style={{ color: "var(--fg-3)" }}
          >
            Don&apos;t have an account?{" "}
            <Link
              to={`${SIGNUP_PATH}${search}`}
              className="font-medium no-underline hover:underline"
              style={{ color: "var(--fg-1)" }}
              onClick={() => trackButtonPressed("Sign up")}
            >
              Sign up
            </Link>
          </p>
        )}
      </div>
    </AuthFrame>
  );
}
